'use client';

import { useEffect, useState } from 'react';
import { Facebook, Twitter, Linkedin } from 'lucide-react';
import { apiGetNewsDetail } from '@/services/newsHome/api';
import { NewsDetailResult } from '@/services/newsHome/type';
import { formatDateVN } from '@/utils/client/date';

// Component icon Pinterest
const PinterestIcon = ({ size = 16, className = "" }: { size?: number, className?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="12" cy="12" r="10" />
        <path d="M8 20l4-9" />
        <path d="M10.7 13c0-2.4 1.3-3.7 3.3-3.7 2.6 0 3 2 3 3.3 0 2.6-1.6 4.3-4 4.3-1 0-1.7-.5-2-1" />
    </svg>
);

// Interface state dữ liệu bài viết
interface NewsArticleState {
    id: string;
    title: string;
    description: string;
    date: string;
    imageUrl?: string;
    contentHtml: string;
    tags: string[];
}

// Interface props nhận ID bài viết
interface DetailsNewsSectionProps {
    slug: string;
}

// Component hiển thị chi tiết bài viết
export default function DetailsNewsSection({ slug }: DetailsNewsSectionProps) {
    // State lưu dữ liệu bài viết
    const [article, setArticle] = useState<NewsArticleState | null>(null);
    // State trạng thái tải dữ liệu
    const [loading, setLoading] = useState(true);
    // State trạng thái lỗi
    const [error, setError] = useState<string | null>(null);

    // Effect lấy dữ liệu chi tiết bài viết theo ID
    useEffect(() => {
        if (!slug) {
            setError('Không tìm thấy mã bài viết');
            setLoading(false);
            return;
        }

        const fetchNews = async () => {
            try {
                setLoading(true);
                setError(null);

                // Gọi API lấy chi tiết bài viết
                const data = await apiGetNewsDetail(slug);

                if (!data || !data.isOK || !data.result) {
                    throw new Error('Không tìm thấy nội dung bài viết');
                }

                const result: NewsDetailResult = data.result;

                // Xử lý danh sách tag từ SEO keywords
                const processedTags = result.seoKeywords
                    ? result.seoKeywords.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag !== '')
                    : [];

                // Gán dữ liệu bài viết vào state
                setArticle({
                    id: result.guid || result.id.toString(),
                    title: result.title || 'Tiêu đề đang cập nhật',
                    // --- CẬP NHẬT: Thay thế \n bằng <br /> ---
                    description: result.description
                        ? result.description.replace(/(?:\r\n|\r|\n)/g, '<br />')
                        : '',
                    // -----------------------------------------
                    date: formatDateVN(result.updatedDateTS),
                    imageUrl: result.image || '',
                    contentHtml: result.content || '<p>Nội dung đang cập nhật...</p>',
                    tags: processedTags,
                });

            } catch (err: any) {
                console.error('Lỗi fetch tin tức:', err);
                setError(err.message || 'Có lỗi xảy ra khi tải bài viết.');
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
    }, [slug]);

    const handleShare = (platform: string) => {
        if (typeof window === 'undefined') return;
        const currentUrl = window.location.href;

        const title = article ? article.title : 'Tin tức Rovi Travel';
        const description = article?.description ? article.description : '';
        const combinedText = description ? `${title} - ${description}` : title;
        const imageUrl = article?.imageUrl || '';

        const encodedUrl = encodeURIComponent(currentUrl);
        const encodedText = encodeURIComponent(combinedText);
        const encodedImage = encodeURIComponent(imageUrl);

        let shareUrl = '';
        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
                break;
            case 'pinterest':
                shareUrl = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedText}`;
                break;
            default:
                return;
        }
        window.open(shareUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
    };

    // Giao diện trạng thái đang tải dữ liệu
    if (loading) {
        return (
            <div className="bg-white py-20 px-4 min-h-[50vh] flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-4 border-gray-200 border-t-green-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500">Đang tải bài viết...</p>
            </div>
        );
    }

    // Giao diện trạng thái lỗi hoặc không có dữ liệu
    if (error || !article) {
        return (
            <div className="bg-white py-20 px-4 text-center min-h-[50vh] flex flex-col items-center justify-center">
                <p className="text-red-600 text-lg mb-2">⚠️ {error || 'Không tìm thấy dữ liệu'}</p>
                <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm transition">
                    Tải lại trang
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full">
            {/* Khối header hiển thị tiêu đề và mô tả bài viết */}
            <div
                className="pt-12 pb-8 px-4 sm:px-6 lg:px-8"
                style={{
                    background: 'linear-gradient(180deg, #F4F2ED 0%, rgba(244, 242, 237, 0.00) 100%)'
                }}
            >
                <div className="max-w-4xl mx-auto" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                    <h1 className="text-3xl xl:text-3xl lg:text-5xl font-medium text-[#2D3746] mb-4 leading-tight">
                        {article.title}
                    </h1>

                    {/* --- HIỂN THỊ HTML CHO DESCRIPTION --- */}
                    {article.description && (
                        <div
                            className="text-lg xl:text-sm text-[#2D3746] mb-6 leading-relaxed font-medium"
                            style={{ fontFamily: '"DM Sans", Arial,Roboto, sans-serif, Plus Jakarta Sans' }}
                            dangerouslySetInnerHTML={{ __html: article.description }}
                        />
                    )}
                    {/* ------------------------------------ */}

                    <div className="border-t border-gray-200 my-6"></div>

                    {/* Khối hiển thị ngày đăng và nút chia sẻ */}
                    <div className="flex flex-row items-center justify-between gap-4 mb-8" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        <div className="text-xs 2xl:text-sm min-[1920px]:text-lg text-[#2D3746] font-normal">
                            <span>{article.date}</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-xs text-[#2D3746] font-semibold mr-1 hidden sm:block">Share</span>
                            {[
                                { id: 'facebook', icon: Facebook },
                                { id: 'linkedin', icon: Linkedin },
                                { id: 'twitter', icon: Twitter },
                                { id: 'pinterest', icon: PinterestIcon }
                            ].map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => handleShare(item.id)}
                                    className="w-7 h-7 flex items-center justify-center rounded-full bg-[#2D3746] text-[#FFFFFF] hover:opacity-90 transition-all duration-200 shadow-sm"
                                >
                                    <item.icon size={14} />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Khối hiển thị ảnh đại diện bài viết */}
                    {article.imageUrl && (
                        <div className="w-full mb-10 rounded-xl overflow-hidden shadow-sm bg-gray-100">
                            <img
                                src={article.imageUrl}
                                alt={article.title}
                                className="w-full h-auto block"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://placehold.co/800x500?text=No+Image';
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Khối nội dung HTML của bài viết */}
            <div className="bg-white pb-20 -mt-10 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto pt-8" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                    <div
                        className="
                            news prose
                            text-[#4A5568] text-base leading-relaxed
                            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
                            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
                            [&_li]:mb-2 [&_li]:pl-1
                            [&_li::marker]:text-[#2D3746] [&_li::marker]:font-bold [&_li::marker]:text-[0.7em]
                        "
                        dangerouslySetInnerHTML={{ __html: article.contentHtml }}
                    />

                    {/* Khối hiển thị tag và chia sẻ cuối bài */}
                    <div className="border-t border-gray-200 mt-8 pt-4 md:mt-12 md:pt-8 flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6">
                        <div className="flex flex-wrap items-center gap-2">
                            {article.tags.length > 0 && (
                                <>
                                    <div className="flex items-center text-[#2D3746] mr-1 ">
                                        <span className="ml-1 text-sm font-semibold" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>TAG</span>
                                    </div>
                                    {article.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 bg-gray-100 text-[#2D3746] bg-[#EEEEEE] text-xs font-medium rounded-full hover:bg-gray-200 transition-colors cursor-default"
                                            style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}>
                                            {tag}
                                        </span>
                                    ))}
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-4 md:gap-2 shrink-0 self-end md:self-auto">
                            <span className="text-[11px] font-semibold text-[#2D3746] flex items-center gap-2">
                                Share
                            </span>
                            <div className="flex items-center gap-2">
                                <button onClick={() => handleShare('facebook')} className="w-6 h-6 flex items-center justify-center bg-[#2D3746] text-[#FFFFFF] rounded-full hover:opacity-90 transition">
                                    <Facebook size={12} />
                                </button>
                                <button onClick={() => handleShare('linkedin')} className="w-6 h-6 flex items-center justify-center bg-[#2D3746] text-[#FFFFFF] rounded-full hover:opacity-90 transition">
                                    <Linkedin size={12} />
                                </button>
                                <button onClick={() => handleShare('twitter')} className="w-6 h-6 flex items-center justify-center bg-[#2D3746] text-[#FFFFFF] rounded-full hover:opacity-90 transition">
                                    <Twitter size={12} />
                                </button>
                                <button onClick={() => handleShare('pinterest')} className="w-6 h-6 flex items-center justify-center bg-[#2D3746] text-[#FFFFFF] rounded-full hover:opacity-90 transition">
                                    <PinterestIcon size={12} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}